import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { pulseCheck } from '~src/services/ApiController';
import { translateContent } from '~src/services/translationServices';
import { fetchTopicQuestions } from '~src/modules/topics/actions/topicActions';
import { MainContainerAnimation } from '~src/styles/animations/containerAnimations.sc';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import DragList from '~src/shared/lists/dragList/DragList';
import logger from '~src/services/logger/LlsLogger';
import { Question } from '~src/interfaces/question.interface';
import { Topic } from '~src/interfaces/topic.interface';
import { StoreI } from '~src/rootReducer';
import TileHeader from '~src/shared/headers/tileHeader/TileHeader';
import { TileContainer } from '~src/styles/containers.sc';
import { ApiReduxResp } from '~src/services/apiMiddleware';

import TopicQuestionListItem from './TopicQuestionListItem';

interface OwnProps {
  topic: Topic;
  defaultContentLocale: string | null;
  contentLocales: { [key: string]: string };
}

interface StateProps {
  currentQuestions: { [key: string]: Question };
  loadingQuestions: boolean;
}

interface DispatchProps {
  fetchTopicQuestions(topicId: string): ApiReduxResp<Question[]>;
}

type Props = OwnProps & StateProps & DispatchProps & WithTranslation;

interface State {
  questionList: { [key: string]: Question };
  loadingQuestions: boolean;
  updating: boolean;
}

interface DefaultProps {
  defaultContentLocale: string | null;
  currentQuestions: { [key: string]: Question };
}

class TopicQuestionList extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    defaultContentLocale: null,
    currentQuestions: {},
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    if (props.currentQuestions !== state.questionList && !state.updating) {
      return { questionList: props.currentQuestions };
    }
    return null;
  }

  state = {
    questionList: this.props.currentQuestions,
    loadingQuestions: true,
    updating: false,
  };

  isUnmounted = false;

  componentDidMount() {
    this.props
      .fetchTopicQuestions(this.props.topic.id)
      .then(res => {
        if (res.error) {
          throw new Error('Error fetching questions of topic');
        }
      })
      .catch(error => {
        logger.info('Error fetching questions of topic', {
          error,
          info: {
            componentStack: 'TopicQuestionList.jsx',
          },
        });
      })
      .finally(() => {
        if (!this.isUnmounted) {
          this.setState({ loadingQuestions: false });
        }
      });
  }

  shouldComponentUpdate(nextProps: Props) {
    return !nextProps.loadingQuestions;
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  onListChange = async (newList: Question[]) => {
    this.setState({ updating: true });
    const tmpQuestionList: { [key: string]: Question } = {};
    newList.forEach(question => {
      tmpQuestionList[question.id] = question;
    });
    this.setState({ questionList: tmpQuestionList });
    const questionIdList: string[] = [];
    newList.forEach(question => {
      questionIdList.push(question.id);
    });
    // TODO make the loading state of the list local that is not loading after changing the order
    await pulseCheck.put(`topics/${this.props.topic.id}/questions/order`, questionIdList).then(() => {
      this.props.fetchTopicQuestions(this.props.topic.id);
    });
    if (!this.isUnmounted) {
      this.setState({ updating: false });
    }
  };

  renderListItem = (item: Question) => <TopicQuestionListItem topicId={this.props.topic.id} item={item} />;

  render() {
    const { t, topic } = this.props;
    const questionArray: Question[] = [];
    Object.keys(this.state.questionList).forEach(key => {
      questionArray.push(this.state.questionList[key]);
    });

    return (
      <MainContainerAnimation>
        <TileContainer style={{ marginTop: 30 }}>
          <TileHeader
            title={`${translateContent(topic.name)} (${questionArray.length})`}
            tooltip={{
              key: 'x.topics.details.questions.list.header.textbox',
              txt: t('x.topics.details.questions.list.header.textbox'),
            }}
          />
          {this.state.loadingQuestions ? (
            <div style={{ paddingBottom: 20 }}>
              <LoadingSpinner />
            </div>
          ) : (
            <DragList
              renderTemplate={this.renderListItem}
              list={questionArray}
              onListChange={this.onListChange}
            />
          )}
        </TileContainer>
      </MainContainerAnimation>
    );
  }
}

const mapStateToProps = ({ topics }: StoreI): StateProps => ({
  currentQuestions: topics.currentQuestions,
  loadingQuestions: topics.loadingQuestions,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchTopicQuestions: (topicId: string) => dispatch(fetchTopicQuestions(topicId)),
});

const TopicQuestionListSuper = connect(mapStateToProps, mapDispatchToProps)(TopicQuestionList);
const TopicQuestionListTranslated = withTranslation()(TopicQuestionListSuper);
export default TopicQuestionListTranslated;
